import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { buildApiUrl } from "../config/api";

import {
  faCartShopping,
  faCircleCheck,
  faFileInvoice,
  faHouse,
  faPaperPlane,
  faArrowRight,
  
  faSpinner,
  faShieldHalved,
  faGlobe,
  faServer,
  faBolt,
} from "@fortawesome/free-solid-svg-icons";

const DOMAIN_EXTENSIONS = [
  { value: ".com" },
  { value: ".net" },
  { value: ".org" },
  { value: ".info" },
  { value: ".in" },
  { value: ".co.in" },
  { value: ".de" },
  { value: ".fr" },
  { value: ".eu" },
  { value: ".co.uk" },
  { value: ".io" },
  { value: ".ai" },
  { value: ".app" },
  { value: ".dev" },
];

function sanitizeDomainLabel(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, "")
    .replace(/^-+/, "")
    .replace(/-+$/, "")
    .slice(0, 30);
}

function formatRupees(amount) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function getAvailabilityBadge(status) {
  if (status === "available") {
    return {
      label: "Available",
      className: "bg-emerald-400 text-emerald-950",
      borderClassName: "border-emerald-200",
    };
  }

  if (status === "unknown") {
    return {
      label: "Availability Unknown",
      className: "bg-orange-400 text-orange-950",
      borderClassName: "border-orange-200",
    };
  }

  return {
    label: "Registered",
    className: "bg-rose-400 text-rose-950",
    borderClassName: "border-rose-200",
  };
}

const features = [
  {
    icon: faShieldHalved,
    title: "Free SSL Security",
    desc: "Protect your website with secure HTTPS encryption.",
  },
  {
    icon: faBolt,
    title: "Lightning Fast DNS",
    desc: "Global DNS infrastructure for blazing-fast performance.",
  },
  {
    icon: faServer,
    title: "Reliable Hosting",
    desc: "High-performance hosting with 99.9% uptime guarantee.",
  },
  {
    icon: faGlobe,
    title: "Global Domain Support",
    desc: "Register domains from popular global extensions.",
  },
];

const whyChooseCardColors = [
  {
    bg: "linear-gradient(135deg, #36e18a 0%, #5df0b3 100%)",
    icon: "bg-white/30 text-emerald-950",
  },
  {
    bg: "linear-gradient(135deg, #ffc648 0%, #ffe394 100%)",
    icon: "bg-white/35 text-amber-950",
  },
  {
    bg: "linear-gradient(135deg, #90d8a2 0%, #62c494 100%)",
    icon: "bg-white/35 text-emerald-950",
  },
  {
    bg: "linear-gradient(135deg, #d1d823 0%, #f3ef5b 100%)",
    icon: "bg-white/35 text-lime-950",
  },
  {
    bg: "linear-gradient(135deg, #46a8eb 0%, #8bd8ff 100%)",
    icon: "bg-white/35 text-blue-950",
  },
  {
    bg: "linear-gradient(135deg, #ff6c38 0%, #ff9f67 100%)",
    icon: "bg-white/35 text-orange-950",
  },
];

const domainPreviewCards = [
  {
    title: "Fresh Crust",
    tone: "from-amber-50 to-orange-100",
    accent: "bg-orange-500",
  },
  {
    title: "Kasm Studio",
    tone: "from-sky-200 to-blue-500",
    accent: "bg-blue-700",
  },
  {
    title: "Print & Bind",
    tone: "from-yellow-100 to-amber-300",
    accent: "bg-amber-700",
  },
];

const popularDomainExtensions = [
  {
    extension: ".com",
    title: "Business Domain",
    price: "Starting from ₹799/year",
    tag: "Most popular",
    accent: "from-blue-50 to-white",
    mark: "bg-blue-500",
    position: "md:left-[4%] md:top-[8%] md:-rotate-[8deg]",
    layer: "md:z-20",
  },
  {
    extension: ".net",
    title: "Network Domain",
    price: "Starting from ₹899/year",
    tag: "Tech ready",
    accent: "from-rose-50 to-white",
    mark: "bg-rose-400",
    position: "md:left-[37%] md:top-[5%] md:rotate-[7deg]",
    layer: "md:z-10",
  },
  {
    extension: ".org",
    title: "Trust Domain",
    price: "Starting from ₹849/year",
    tag: "Nonprofit",
    accent: "from-yellow-50 to-white",
    mark: "bg-amber-400",
    position: "md:right-[5%] md:top-[11%] md:rotate-[8deg]",
    layer: "md:z-20",
  },
  {
    extension: ".info",
    title: "Info Domain",
    price: "Starting from Rs.2,499/year",
    tag: "Content ready",
    accent: "from-emerald-50 to-white",
    mark: "bg-emerald-500",
    position: "md:left-[5%] md:bottom-[6%] md:rotate-[5deg]",
    layer: "md:z-30",
  },
  {
    extension: ".io",
    title: "Startup Domain",
    price: "Starting from ₹2,999/year",
    tag: "Product teams",
    accent: "from-violet-50 to-white",
    mark: "bg-violet-500",
    position: "md:left-[29%] md:bottom-[8%] md:rotate-[6deg]",
    layer: "md:z-30",
  },
  {
    extension: ".in",
    title: "India Domain",
    price: "Starting from ₹599/year",
    tag: "Local brand",
    accent: "from-sky-50 to-white",
    mark: "bg-sky-500",
    position: "md:left-[48%] md:bottom-[20%] md:-rotate-[3deg]",
    layer: "md:z-40",
  },
  {
    extension: ".co",
    title: "Company Domain",
    price: "Starting from ₹1,199/year",
    tag: "Short name",
    accent: "from-orange-50 to-white",
    mark: "bg-orange-400",
    position: "md:right-[10%] md:bottom-[7%] md:-rotate-[6deg]",
    layer: "md:z-30",
  },
];

const Domain = () => {
  const navigate = useNavigate();

  const [domainLabelInput, setDomainLabelInput] = useState("");
  const [domainExtension, setDomainExtension] = useState(".com");
  const [domainResult, setDomainResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showQuoteModal, setShowQuoteModal] =
  useState(false);
  const [showLeadForm, setShowLeadForm] =
  useState(false);

const [selectedPackage, setSelectedPackage] =
  useState("");

const [leadForm, setLeadForm] = useState({
  fullName: "",
  email: "",
  phone: "",
  description: "",
});

  const handleDomainSearch = async (event) => {
    event.preventDefault();

    const cleanedLabel = sanitizeDomainLabel(domainLabelInput);

    if (!cleanedLabel) {
      setDomainResult({
        error:
          "Enter a valid domain name using letters, numbers, or hyphen.",
      });
      return;
    }

    const domain = `${cleanedLabel}${domainExtension}`;

    setLoading(true);
    setDomainResult(null);

    try {
      const res = await fetch(
        buildApiUrl(
          `/api/domain-check?domain=${encodeURIComponent(domain)}`
        )
      );

      const data = await res.json();

      if (!res.ok || data.error) {
        setDomainResult({
          error: data.error || "Failed to check this domain.",
        });
      } else {
        setDomainResult({
          fullDomain: data.domain,
          isAvailable: data.isAvailable,
          availabilityStatus:
            data.availabilityStatus ||
            (data.isAvailable ? "available" : "registered"),
          registrationPrice: data.registrationPrice,
          renewalPrice: data.renewalPrice,
        });
      }
    } catch (err) {
      setDomainResult({
        error: "Failed to check domain. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };
const handleLeadSubmit = async () => {
  try {
    const response = await fetch(
      buildApiUrl("/api/send-quotation-lead"),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          packageName: selectedPackage,
          ...leadForm,
        }),
      }
    );

    const data = await response.json();

    if (data.success) {
      alert("Quotation request sent successfully!");

      setShowLeadForm(false);

      setLeadForm({
        fullName: "",
        email: "",
        phone: "",
        description: "",
      });
    }
  } catch (err) {
    console.log(err);

    alert("Failed to send request");
  }
};


  // Navbar is fixed with h-[64px] (sm:h-[64px]), so add pt-32 (8rem/128px) for safety
  return (
    <main className="relative isolate min-h-screen overflow-hidden bg-gradient-to-br from-slate-950 via-violet-950 to-slate-900 px-4 pt-[112px] text-white sm:px-8 lg:px-10">

      {/* BACKGROUND EFFECTS */}
      <div className="absolute left-10 top-20 h-72 w-72 rounded-full bg-violet-500/20 blur-3xl"></div>

      <div className="absolute bottom-10 right-10 h-96 w-96 rounded-full bg-fuchsia-500/20 blur-3xl"></div>

      {/* TOPBAR */}
      <div className="relative z-10 mx-auto flex w-full max-w-7xl items-center justify-between gap-4">

        <button
          type="button"
          title="Home"
          aria-label="Go to home page"
          onClick={() => navigate("/")}
          className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-white/10 text-white backdrop-blur-xl transition-all duration-300 hover:scale-105 hover:bg-white/20"
        >
          <FontAwesomeIcon icon={faHouse} />
        </button>

        <div className="inline-flex min-h-12 items-center gap-3 rounded-full border border-white/10 bg-white/10 px-5 py-2 text-sm font-bold text-white backdrop-blur-xl">
  <FontAwesomeIcon icon={faGlobe} />
  <span>Live Domain Search</span>
</div>

      </div>

      {/* HERO SECTION */}
      <section className="relative z-10 mx-auto mt-8 w-full max-w-7xl overflow-hidden rounded-[32px] border border-sky-100 bg-gradient-to-br from-white via-sky-50 to-violet-50 px-5 py-8 text-slate-950 shadow-[0_26px_80px_rgba(37,99,235,0.18)] sm:px-8 lg:px-12 lg:py-10">

        <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(59,130,246,0.08),transparent_42%,rgba(168,85,247,0.12))]"></div>
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-blue-300 to-transparent"></div>

        <div className="relative grid gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
          <div className="max-w-2xl">
            <p className="text-xs font-black uppercase tracking-[0.36em] text-fuchsia-600 sm:text-sm">
              Premium Domain Marketplace
            </p>

            <h1 className="mt-5 text-4xl font-black leading-[1.03] text-slate-950 sm:text-5xl md:text-6xl xl:text-7xl">
              Register Your{" "}
              <span className="bg-gradient-to-r from-violet-600 via-fuchsia-500 to-blue-600 bg-clip-text text-transparent">
                Perfect
              </span>
              <span className="block bg-gradient-to-r from-fuchsia-500 via-violet-600 to-blue-600 bg-clip-text text-transparent">
                Domain
              </span>
            </h1>

            <p className="mt-6 max-w-xl text-base font-medium leading-8 text-slate-600">
              Search domain availability, compare pricing,
              and launch your website with hosting,
              SSL security, and deployment services.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">

              <button
                type="button"
onClick={() => setShowQuoteModal(true)}
                className="inline-flex h-12 items-center gap-3 rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 text-sm font-bold text-white shadow-[0_14px_34px_rgba(168,85,247,0.32)] transition-all duration-300 hover:-translate-y-1"
              >
                <FontAwesomeIcon icon={faFileInvoice} />
                Get Quotation
              </button>

              <button
                type="button"
                onClick={() => navigate("/contact")}
                className="inline-flex h-12 items-center gap-3 rounded-2xl border border-slate-300 bg-white/85 px-6 text-sm font-bold text-slate-700 shadow-[0_12px_28px_rgba(15,23,42,0.1)] transition-all duration-300 hover:-translate-y-1 hover:border-blue-300 hover:text-blue-700"
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                Contact Us
              </button>

            </div>
          </div>

          <div
            className="relative mx-auto h-[330px] w-full max-w-[620px] sm:h-[380px] lg:h-[390px]"
            aria-hidden="true"
          >
            <div className="absolute inset-3 rounded-[34px] border border-white/80 bg-white/60 shadow-inner"></div>
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_60%_42%,rgba(147,197,253,0.45),transparent_48%)]"></div>

            <div className="domain-hero-pop absolute left-[8%] top-[16%] z-20 sm:left-[14%]">
              <div className="w-[210px] rotate-[-7deg] rounded-[22px] border border-white bg-white/95 p-3 shadow-[0_22px_45px_rgba(59,130,246,0.22)] sm:w-[250px]">
                <div className="flex items-center gap-1">
                  <span className="h-2 w-2 rounded-full bg-rose-300"></span>
                  <span className="h-2 w-2 rounded-full bg-amber-300"></span>
                  <span className="h-2 w-2 rounded-full bg-emerald-300"></span>
                  <span className="ml-3 h-2 flex-1 rounded-full bg-slate-100"></span>
                </div>

                <div className="mt-3 grid h-28 place-items-center rounded-2xl bg-gradient-to-br from-violet-100 via-pink-100 to-sky-100 sm:h-32">
                  <div className="grid h-16 w-16 place-items-center rounded-2xl bg-white text-3xl text-violet-600 shadow-lg">
                    <FontAwesomeIcon icon={faShieldHalved} />
                  </div>
                </div>
              </div>
            </div>

            <div className="domain-hero-pop-slow domain-hero-delay-1 absolute right-[11%] top-[8%] z-30">
              <div className="h-48 w-24 rotate-[8deg] rounded-[24px] bg-slate-900 p-4 text-white shadow-[0_26px_50px_rgba(15,23,42,0.28)] sm:h-56 sm:w-28">
                <div className="grid h-10 w-10 place-items-center rounded-2xl bg-violet-500 text-lg">
                  <FontAwesomeIcon icon={faServer} />
                </div>

                <div className="mt-6 space-y-3">
                  <span className="block h-2 rounded-full bg-white/35"></span>
                  <span className="block h-2 rounded-full bg-white/25"></span>
                  <span className="block h-2 rounded-full bg-violet-300"></span>
                  <span className="block h-2 rounded-full bg-white/25"></span>
                </div>
              </div>
            </div>

            <div className="domain-hero-pop domain-hero-delay-2 absolute bottom-[15%] right-[3%] z-20 sm:right-[8%]">
              <div className="grid h-28 w-28 rotate-[-10deg] place-items-center rounded-full border border-blue-200 bg-gradient-to-br from-white to-sky-100 text-5xl text-blue-600 shadow-[0_22px_45px_rgba(37,99,235,0.22)] sm:h-32 sm:w-32">
                <FontAwesomeIcon icon={faGlobe} />
              </div>
            </div>

            <div className="domain-hero-pop-slow domain-hero-delay-3 absolute bottom-[8%] left-[24%] z-10">
              <div className="h-24 w-40 rotate-[12deg] rounded-[22px] border border-white bg-white/90 p-3 shadow-[0_18px_40px_rgba(236,72,153,0.2)] sm:h-28 sm:w-48">
                <div className="h-4 w-24 rounded-full bg-pink-200"></div>
                <div className="mt-4 grid grid-cols-[1fr_0.7fr] gap-3">
                  <div className="space-y-2">
                    <span className="block h-3 rounded-full bg-sky-200"></span>
                    <span className="block h-3 rounded-full bg-violet-200"></span>
                    <span className="block h-3 w-3/4 rounded-full bg-slate-200"></span>
                  </div>
                  <div className="rounded-xl bg-gradient-to-br from-pink-300 to-blue-300"></div>
                </div>
              </div>
            </div>

            <div className="domain-hero-pop domain-hero-delay-1 absolute bottom-[24%] left-[4%] z-30 sm:left-[9%]">
              <div className="grid h-16 w-16 rotate-[14deg] place-items-center rounded-2xl border border-white bg-violet-600 text-2xl text-white shadow-[0_18px_36px_rgba(124,58,237,0.28)]">
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
            </div>

            <div className="domain-hero-pop-slow domain-hero-delay-2 absolute left-[42%] top-[5%] z-10">
              <div className="rounded-full border border-blue-200 bg-white px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-blue-700 shadow-[0_14px_30px_rgba(37,99,235,0.15)]">
                .com .net .info .in
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* SEARCH SECTION */}
      <section className="relative z-10 mx-auto mt-12 w-full max-w-7xl overflow-hidden rounded-[28px] border border-sky-100 bg-gradient-to-b from-white via-sky-50 to-blue-100 px-5 py-10 text-slate-950 shadow-[0_28px_90px_rgba(37,99,235,0.22)] sm:px-8 md:py-12">

        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-blue-200/70 to-transparent"></div>

        <div className="relative mx-auto max-w-4xl text-center">
          <h2 className="mx-auto max-w-3xl text-4xl font-semibold leading-tight text-black md:text-6xl">
            Buy your perfect
            <span className="block">domain name</span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-sm font-medium leading-6 text-slate-700 md:text-base">
            Get online confidently with SSL protection, reliable web hosting and 24/7 support. Plus, claim a free domain for a year with any annual website plan.
          </p>
        </div>

        <form
          onSubmit={handleDomainSearch}
          className="relative mx-auto mt-7 flex w-full max-w-2xl flex-col rounded-[30px] border border-blue-500/70 bg-white p-2 shadow-[0_18px_45px_rgba(37,99,235,0.22)] sm:flex-row sm:items-center"
        >

          {/* DOMAIN INPUT */}
          <label className="min-w-0 flex-1">

            <span className="sr-only">
              Domain Name
            </span>

            <input
              type="text"
              value={domainLabelInput}
              onChange={(event) => {
                setDomainLabelInput(event.target.value);
                setDomainResult(null);
              }}
              placeholder="Type the domain you want"
              className="h-12 w-full rounded-[24px] bg-transparent px-5 text-sm font-medium text-slate-950 outline-none transition placeholder:text-slate-500 focus:bg-sky-50"
              required
            />

          </label>

          {/* EXTENSION */}
          <label className="mt-2 border-y border-slate-200 py-1 sm:mt-0 sm:border-x sm:border-y-0 sm:py-0">

            <span className="sr-only">
              Extension
            </span>

            <select
              value={domainExtension}
              onChange={(event) => {
                setDomainExtension(event.target.value);
                setDomainResult(null);
                
              }}
              className="h-12 w-full cursor-pointer rounded-[24px] bg-white px-4 text-sm font-bold text-slate-950 outline-none transition focus:bg-sky-50 sm:w-28"
            >
              {DOMAIN_EXTENSIONS.map((extension) => (
                <option
                  key={extension.value}
                  value={extension.value}
                  className="bg-white text-slate-950"
                >
                  {extension.value}
                </option>
              ))}
            </select>

          </label>

          {/* BUTTON */}
          <button
            type="submit"
            disabled={loading}
            className="mt-2 inline-flex h-12 items-center justify-center gap-3 rounded-[24px] bg-blue-600 px-7 text-sm font-bold text-white shadow-[0_10px_24px_rgba(37,99,235,0.35)] transition-all duration-300 hover:bg-blue-700 disabled:opacity-60 sm:mt-0"
          >

            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                Searching
              </>
            ) : (
              <>
                <span>Search</span>
                <FontAwesomeIcon icon={faArrowRight} />
              </>
            )}

          </button>

        </form>

        {!domainResult && (
          <div className="relative mt-14 grid gap-6 md:grid-cols-3">
            {domainPreviewCards.map((card, index) => (
              <article
                key={card.title}
                className={`min-h-[150px] overflow-hidden rounded-xl bg-gradient-to-br ${card.tone} shadow-[0_18px_40px_rgba(15,23,42,0.16)] ring-1 ring-black/5`}
              >
                {index === 0 && (
                  <div className="grid h-full grid-cols-[1fr_1.05fr]">
                    <div className="relative bg-stone-100 p-4">
                      <div className="absolute left-5 top-5 h-20 w-20 rotate-12 rounded-[18px] border-[10px] border-white bg-amber-300 shadow-lg"></div>
                      <div className="absolute bottom-5 right-4 h-14 w-20 rounded-full bg-amber-700/20"></div>
                    </div>
                    <div className="flex flex-col justify-between bg-orange-50 p-4 text-right">
                      <div className="flex justify-end gap-2 text-[8px] font-bold uppercase text-orange-900/60">
                        <span>Shop</span>
                        <span>About</span>
                        <span>Cart</span>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold uppercase text-orange-700">
                          Pretzel
                        </p>
                        <h3 className="text-2xl font-black leading-none text-orange-950">
                          Perfect
                        </h3>
                      </div>
                      <span className="ml-auto inline-flex rounded-full bg-orange-700 px-3 py-1 text-[8px] font-black uppercase text-white">
                        Order now
                      </span>
                    </div>
                  </div>
                )}

                {index === 1 && (
                  <div className="relative h-full p-4 text-white">
                    <div className="mx-auto mb-3 h-5 w-36 rounded-full bg-slate-950/80 px-3 py-1 text-center text-[9px] font-bold">
                      www.kasmstudio.net
                    </div>
                    <div className="absolute left-8 top-16 h-12 w-16 rotate-[-18deg] rounded-2xl bg-white/70 shadow-lg"></div>
                    <div className="absolute right-10 top-14 h-10 w-10 rotate-12 rounded-xl bg-pink-300 shadow-lg"></div>
                    <div className="absolute bottom-0 left-1/2 h-28 w-24 -translate-x-1/2 rounded-t-full bg-slate-900"></div>
                    <div className="absolute bottom-14 left-1/2 h-14 w-14 -translate-x-1/2 rounded-full bg-amber-100"></div>
                    <div className="absolute bottom-5 left-4 flex gap-1">
                      <span className="h-2 w-8 rounded-full bg-white/70"></span>
                      <span className="h-2 w-2 rounded-full bg-white/70"></span>
                      <span className="h-2 w-2 rounded-full bg-white/70"></span>
                    </div>
                  </div>
                )}

                {index === 2 && (
                  <div className="flex h-full flex-col p-4">
                    <div className="flex items-center justify-between text-[8px] font-bold uppercase text-amber-950/60">
                      <span>{card.title}</span>
                      <span>Portfolio</span>
                      <span>Contact</span>
                    </div>
                    <h3 className="mt-3 text-center text-2xl font-semibold text-amber-950">
                      Print & Bind
                    </h3>
                    <div className="relative mt-4 flex flex-1 items-end justify-center rounded-lg bg-orange-300/70 p-3">
                      <div className="h-16 w-10 rotate-[-10deg] rounded bg-amber-900 shadow-lg"></div>
                      <div className="h-20 w-10 rotate-[-4deg] rounded bg-blue-700 shadow-lg"></div>
                      <div className="h-14 w-10 rotate-[10deg] rounded bg-indigo-300 shadow-lg"></div>
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}

        {/* ERROR */}
        {domainResult?.error && (
          <div className="relative mt-8 rounded-2xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm font-semibold text-rose-700">
            {domainResult.error}
          </div>
        )}

        {/* RESULT */}
        {domainResult && !domainResult.error && (
          (() => {
            const availabilityStatus =
              domainResult.availabilityStatus ||
              (domainResult.isAvailable ? "available" : "registered");
            const badge = getAvailabilityBadge(availabilityStatus);

            return (
          <article
            className={`relative mt-10 rounded-[26px] border bg-white p-6 shadow-[0_18px_45px_rgba(15,23,42,0.12)] md:p-8 ${
              badge.borderClassName
            }`}
          >

            <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between">

              <div>
                <p className="text-sm font-bold uppercase tracking-[0.2em] text-slate-500">
                  Domain Name
                </p>

                <h3 className="mt-3 break-words text-3xl font-black text-slate-950 md:text-4xl">
                  {domainResult.fullDomain}
                </h3>
              </div>

              <span
                className={`inline-flex w-fit items-center gap-2 rounded-full px-5 py-2 text-sm font-bold uppercase ${
                  badge.className
                }`}
              >
                <FontAwesomeIcon icon={faCircleCheck} />

                {badge.label}
              </span>

            </div>

            {availabilityStatus === "unknown" && (
              <div className="mt-6 rounded-2xl border border-orange-200 bg-orange-50 px-5 py-4 text-sm font-semibold text-orange-800">
                Registry lookup is temporarily unavailable. Please try again later.
              </div>
            )}

            <div className="mt-8 grid gap-5 md:grid-cols-2">

              <div className="rounded-2xl border border-slate-200 bg-sky-50 p-5">
                <p className="text-sm font-semibold text-slate-600">
                  Registration Price
                </p>

                <h4 className="mt-3 text-3xl font-black text-blue-700">
                  {formatRupees(
                    domainResult.registrationPrice
                  )}
                </h4>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-sky-50 p-5">
                <p className="text-sm font-semibold text-slate-600">
                  Renewal Price
                </p>

                <h4 className="mt-3 text-3xl font-black text-blue-700">
                  {formatRupees(domainResult.renewalPrice)}
                </h4>
              </div>

            </div>

          {availabilityStatus === "available" && (
  <div className="mt-8 flex flex-wrap gap-4">

    <button
      type="button"
      onClick={() =>
        navigate("/payment/razorpay", {
          state: {
            orderType: "domain",
            domain: domainResult.fullDomain,

            serviceKey: "new_domain",
            domainSource: "asiduo_registered",
            serviceCategory: "domain",
            setupPreference: "managed_dns_ssl",
            requestedAddOns: {
              dns: true,
              ssl: true,
              businessEmail: false,
              hosting: false,
            },

            amount:
              domainResult.registrationPrice,

            registrationPrice:
              domainResult.registrationPrice,

            renewalPrice:
              domainResult.renewalPrice,
          },
        })
      }
      className="inline-flex h-12 items-center gap-3 rounded-2xl bg-blue-600 px-6 text-sm font-bold text-white transition-all duration-300 hover:bg-blue-700"
    >
      <FontAwesomeIcon icon={faCartShopping} />

      Book Domain
    </button>

    <button
      type="button"
      onClick={() => navigate("/email", {
        state: {
          domainName: domainResult.fullDomain,
          domainSource: "asiduo_registered",
        },
      })}
      className="inline-flex h-12 items-center gap-3 rounded-2xl border border-blue-200 bg-white px-6 text-sm font-bold text-blue-700 transition-all duration-300 hover:border-blue-400 hover:bg-blue-50"
    >
      Add Business Email
    </button>

  </div>
)}

{availabilityStatus === "registered" && (
  <div className="mt-8 flex flex-wrap gap-4">
    <button
      type="button"
      onClick={() => navigate("/email", {
        state: {
          domainName: domainResult.fullDomain,
          domainSource: "external",
        },
      })}
      className="inline-flex h-12 items-center gap-3 rounded-2xl bg-blue-600 px-6 text-sm font-bold text-white transition-all duration-300 hover:bg-blue-700"
    >
      Buy Email For This Domain
    </button>

    <button
      type="button"
      onClick={() => navigate("/ssl", {
        state: {
          domainName: domainResult.fullDomain,
          domainSource: "external",
        },
      })}
      className="inline-flex h-12 items-center gap-3 rounded-2xl border border-blue-200 bg-white px-6 text-sm font-bold text-blue-700 transition-all duration-300 hover:border-blue-400 hover:bg-blue-50"
    >
      Setup SSL
    </button>
  </div>
)}

          </article>
            );
          })()
        )}

      </section>

      <section className="relative z-10 mx-auto mt-12 w-full max-w-7xl overflow-hidden rounded-[34px] border border-white/70 bg-gradient-to-br from-slate-100 via-violet-50 to-slate-200 px-5 py-12 shadow-[0_28px_80px_rgba(15,23,42,0.18)] sm:px-8">
        <div className="absolute -left-10 bottom-6 h-32 w-32 rounded-full bg-violet-300/45 blur-2xl"></div>
        <div className="absolute right-8 top-8 h-40 w-40 rounded-full bg-pink-200/45 blur-3xl"></div>

        <div className="relative mx-auto flex max-w-5xl flex-col items-center gap-5 md:min-h-[360px] md:flex-row md:items-end md:justify-center">
        {[
          {
            title: "Domain Bought From Us",
            body:
              "After payment, the backend verifies Razorpay, sends registrant details to GoDaddy, registers the domain, then continues DNS, hosting, email, and SSL setup.",
            label: "Managed registration",
            gradient:
              "from-sky-500 via-violet-500 to-fuchsia-500",
            tilt: "md:rotate-[-2deg]",
            size: "md:h-[285px]",
          },
          {
            title: "Existing Customer Domain",
            body:
              "If the customer already owns the domain elsewhere, keep it with the current registrar. We provide exact DNS records or ask them to switch nameservers when they want Asiduo to manage DNS.",
            label: "DNS guidance",
            gradient:
              "from-indigo-500 via-fuchsia-500 to-orange-400",
            tilt: "md:rotate-[2deg]",
            size: "md:h-[335px]",
          },
          {
            title: "Email And SSL Add-ons",
            body:
              "Business email needs domain ownership verification plus MX, SPF, DKIM, and DMARC records. SSL needs the website DNS to point to hosting or a TXT/HTTP validation record.",
            label: "Security setup",
            gradient:
              "from-orange-500 via-rose-500 to-violet-800",
            tilt: "md:rotate-[1deg]",
            size: "md:h-[285px]",
          },
        ].map((item) => (
          <article
            key={item.title}
            className={`group relative flex min-h-[285px] w-full max-w-[270px] flex-col justify-end overflow-hidden rounded-[18px] bg-gradient-to-br ${item.gradient} p-5 text-white shadow-[0_24px_48px_rgba(15,23,42,0.2)] transition-all duration-500 hover:-translate-y-4 hover:rotate-0 ${item.size} ${item.tilt}`}
          >
            <div className="absolute -left-10 top-6 h-44 w-44 rounded-full bg-white/25 blur-sm transition-transform duration-500 group-hover:scale-125"></div>
            <div className="absolute right-[-36px] top-[-18px] h-36 w-36 rounded-full bg-black/20 blur-xl"></div>
            <div className="absolute inset-0 bg-[linear-gradient(135deg,rgba(255,255,255,0.26),transparent_34%,rgba(15,23,42,0.28))]"></div>
            <div className="absolute left-0 top-0 h-full w-full opacity-60">
              <div className="absolute left-[-18%] top-[18%] h-24 w-[150%] rotate-[-35deg] rounded-full bg-white/20"></div>
              <div className="absolute right-[-25%] top-[36%] h-20 w-[115%] rotate-[28deg] rounded-full bg-purple-950/25"></div>
            </div>

            <div className="relative">
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-white/75">
                {item.label}
              </p>

              <h3 className="mt-2 text-2xl font-black leading-tight text-white">
                {item.title}
              </h3>

              <p className="mt-3 max-h-24 overflow-hidden text-xs font-medium leading-5 text-white/85">
                {item.body}
              </p>
            </div>

            <div className="absolute bottom-4 right-4 grid h-9 w-9 place-items-center rounded-full bg-white/15 text-sm text-white backdrop-blur-md transition-all duration-300 group-hover:bg-white group-hover:text-violet-700">
              <FontAwesomeIcon icon={faArrowRight} />
            </div>
          </article>
        ))}
        </div>
      </section>

      {/* FEATURES */}
      <section className="relative z-10 mx-auto mt-16 w-full max-w-7xl overflow-hidden rounded-[34px] border border-white/70 bg-gradient-to-br from-white via-sky-50 to-violet-100 px-5 py-12 shadow-[0_28px_80px_rgba(37,99,235,0.16)] sm:px-8">

        <div className="absolute left-8 top-8 h-32 w-32 rounded-full bg-blue-200/50 blur-3xl"></div>
        <div className="absolute bottom-8 right-10 h-36 w-36 rounded-full bg-fuchsia-200/50 blur-3xl"></div>

        <h2 className="relative text-center text-4xl font-black text-slate-950 md:text-6xl">
          Why Choose Us
        </h2>

        <div className="relative mt-12 overflow-hidden">
          <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-20 bg-gradient-to-r from-white via-white/80 to-transparent"></div>
          <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-20 bg-gradient-to-l from-violet-100 via-violet-100/80 to-transparent"></div>

          <div className="why-choose-marquee flex w-max gap-6 py-5">
            {[...features, ...features].map((feature, index) => {
              const color = whyChooseCardColors[index % whyChooseCardColors.length];

              return (
                <article
                  key={`${feature.title}-${index}`}
                  style={{ background: color.bg }}
                  className={`group flex min-h-[250px] w-[280px] shrink-0 flex-col rounded-[24px] border border-white/70 p-6 text-slate-950 shadow-[0_18px_40px_rgba(15,23,42,0.12)] transition-all duration-500 hover:-translate-y-3 hover:rotate-0 ${
                    index % 4 === 0
                      ? "-rotate-2"
                      : index % 4 === 1
                      ? "rotate-1"
                      : index % 4 === 2
                      ? "-rotate-1"
                      : "rotate-2"
                  }`}
                >

                <div className={`flex h-16 w-16 items-center justify-center rounded-2xl text-2xl shadow-inner ${color.icon}`}>

                  <FontAwesomeIcon icon={feature.icon} />

                </div>

                <h3 className="mt-7 text-2xl font-black leading-tight text-slate-950">
                  {feature.title}
                </h3>

                <p className="mt-4 leading-7 text-slate-600">
                  {feature.desc}
                </p>

                

              </article>
              );
            })}
          </div>

        </div>

      </section>

      {/* DOMAIN EXTENSIONS */}
      <section className="relative z-10 mx-auto mt-20 w-full max-w-7xl">

        <h2 className="text-center text-4xl font-black text-white md:text-6xl">
          Popular Domain Extensions
        </h2>

        <div className="relative mx-auto mt-14 max-w-5xl overflow-hidden rounded-[34px] border border-white/70 bg-gradient-to-br from-[#f1eaff] via-[#fbf9ff] to-[#e8dcff] px-5 py-10 shadow-[0_30px_90px_rgba(109,40,217,0.22)] sm:px-8 md:min-h-[500px] lg:min-h-[480px]">
          <div className="absolute left-1/2 top-1/2 h-80 w-80 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/65 blur-3xl"></div>
          <div className="absolute right-10 top-10 h-32 w-32 rounded-full bg-pink-200/50 blur-3xl"></div>
          <div className="absolute bottom-8 left-12 h-36 w-36 rounded-full bg-blue-200/50 blur-3xl"></div>

          <div className="relative grid gap-5 sm:grid-cols-2 md:absolute md:inset-0 md:block">

            {popularDomainExtensions.map((domain) => (
              <article
                key={domain.extension}
                className={`group rounded-2xl border border-white bg-gradient-to-br ${domain.accent} p-4 text-slate-950 shadow-[0_18px_36px_rgba(88,28,135,0.14)] transition-all duration-500 hover:z-50 hover:-translate-y-4 hover:scale-105 hover:rotate-0 md:absolute md:w-[218px] lg:w-[240px] ${domain.position} ${domain.layer}`}
              >

                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className={`h-3 w-3 rounded-full ${domain.mark}`}></span>
                    <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                      {domain.extension}
                    </span>
                  </div>

                  <span className="rounded-full bg-white px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-violet-600 shadow-sm">
                    Popular
                  </span>
                </div>

                <h3 className="mt-5 text-xl font-black leading-tight text-slate-950">
                  {domain.title}
                </h3>

                <p className="mt-2 text-sm font-bold text-slate-600">
                  {domain.price}
                </p>

                <div className="mt-5 flex items-center justify-between border-t border-slate-200 pt-3">
                  <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">
                    {domain.tag}
                  </span>

                  <span className="text-xs font-black uppercase tracking-[0.16em] text-violet-500">
                    Register
                  </span>
                </div>

                <div className="mt-4 h-2 rounded-full bg-gradient-to-r from-transparent via-violet-200 to-transparent opacity-80"></div>

              </article>
            ))}

          </div>

        </div>

      </section>

    {showQuoteModal && (
<div className="fixed inset-0 z-[200] overflow-y-auto bg-black/70 px-4 pt-[140px] pb-10 backdrop-blur-sm"
style={{
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  }}>
        <div className="relative mx-auto- w-full max-w-5xl rounded-[35px] border border-white/10 bg-slate-950 p-8 text-white shadow-2xl">

          <button
            type="button"
            onClick={() => setShowQuoteModal(false)}
            className="absolute right-5 top-5 rounded-full bg-white/10 px-4 py-2 text-sm font-bold text-white"
          >
            ✕
          </button>

          <h2 className="text-center text-4xl font-black">
            Website & Domain Packages
          </h2>

          <p className="mt-4 text-center text-slate-300">
            Launch your business online with complete domain,
            hosting, deployment, and SEO solutions.
          </p>

          <div className="mt-12 grid gap-8 md:grid-cols-3">

            <div className="rounded-[30px] border border-white/10 bg-white/5 p-6">
              <h3 className="text-2xl font-black text-fuchsia-400">
                Starter Launch
              </h3>

              <p className="mt-4 text-5xl font-black">
                ₹4,999
              </p>

              <ul className="mt-6 space-y-3 text-sm text-slate-300">
                <li>✔ Domain Registration</li>
                <li>✔ Hosting Setup</li>
                <li>✔ Free SSL</li>
                <li>✔ 1 Business Email</li>
                <li>✔ Mobile Responsive</li>
              </ul>

              <button
                type="button"
onClick={() => {
  setSelectedPackage("Starter Launch");
  setShowLeadForm(true);
}}
                className="mt-8 w-full rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-5 py-3 text-sm font-bold"
              >
                Contact Team
              </button>
            </div>

            <div className="rounded-[30px] border border-fuchsia-500/40 bg-fuchsia-500/10 p-6">
              <h3 className="text-2xl font-black text-fuchsia-300">
                Business Website
              </h3>

              <p className="mt-4 text-5xl font-black">
                ₹14,999
              </p>

              <ul className="mt-6 space-y-3 text-sm text-slate-200">
                <li>✔ Premium UI Design</li>
                <li>✔ SEO Optimization</li>
                <li>✔ WhatsApp Integration</li>
                <li>✔ Hosting + SSL</li>
                <li>✔ Admin Panel</li>
              </ul>

              <button
                type="button"
onClick={() => {
  setSelectedPackage("Business Website");
  setShowLeadForm(true);
}}
                className="mt-8 w-full rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-5 py-3 text-sm font-bold"
              >
                Contact Team
              </button>
            </div>

            <div className="rounded-[30px] border border-white/10 bg-white/5 p-6">
              <h3 className="text-2xl font-black text-sky-400">
                Premium Growth
              </h3>

              <p className="mt-4 text-5xl font-black">
                ₹29,999
              </p>

              <ul className="mt-6 space-y-3 text-sm text-slate-300">
                <li>✔ Custom Development</li>
                <li>✔ Payment Integration</li>
                <li>✔ Advanced SEO</li>
                <li>✔ Cloud Hosting</li>
                <li>✔ Priority Support</li>
              </ul>

              <button
                type="button"
onClick={() => {
  setSelectedPackage("Premium Growth");
  setShowLeadForm(true);
}}

                className="mt-8 w-full rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-5 py-3 text-sm font-bold"
              >
                Contact Team
              </button>
            </div>

          </div>

        </div>

      </div>
    )}
    {showLeadForm && (
<div className="fixed inset-0 z-[200] overflow-y-auto bg-black/80 px-4 pt-[140px] pb-10 backdrop-blur-sm"
style={{
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  }}>
    <div className="relative mx-auto  w-full max-w-2xl rounded-[35px] border border-white/10 bg-slate-950 p-8 text-white shadow-2xl">

      <button
        type="button"
        onClick={() => setShowLeadForm(false)}
        className="absolute right-5 top-5 rounded-full bg-white/10 px-4 py-2 text-sm font-bold"
      >
        ✕
      </button>

      <h2 className="text-4xl font-black">
        Request Quotation
      </h2>

      <p className="mt-3 text-slate-300">
        Fill the form and our team will contact you shortly.
      </p>

      <div className="mt-8 grid gap-5">

        <div>
          <label className="mb-2 block text-sm font-bold">
            Selected Package
          </label>

          <input
            type="text"
            value={selectedPackage}
            readOnly
            className="w-full rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-white outline-none"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-bold">
            Full Name
          </label>

          <input
            type="text"
            value={leadForm.fullName}
            onChange={(e) =>
              setLeadForm({
                ...leadForm,
                fullName: e.target.value,
              })
            }
            className="w-full rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-white outline-none"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-bold">
            Email
          </label>

          <input
            type="email"
            value={leadForm.email}
            onChange={(e) =>
              setLeadForm({
                ...leadForm,
                email: e.target.value,
              })
            }
            className="w-full rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-white outline-none"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-bold">
            Contact Number
          </label>

          <input
            type="text"
            value={leadForm.phone}
            onChange={(e) =>
              setLeadForm({
                ...leadForm,
                phone: e.target.value,
              })
            }
            className="w-full rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-white outline-none"
          />
        </div>

        <div>
          <label className="mb-2 block text-sm font-bold">
            Description
          </label>

          <textarea
            rows="5"
            value={leadForm.description}
            onChange={(e) =>
              setLeadForm({
                ...leadForm,
                description: e.target.value,
              })
            }
            className="w-full rounded-2xl border border-white/10 bg-white/5 px-5 py-4 text-white outline-none"
          />
        </div>

        <button
          type="button"
          onClick={handleLeadSubmit}
          className="mt-4 rounded-2xl bg-gradient-to-r from-violet-600 to-fuchsia-600 px-6 py-4 text-sm font-bold"
        >
          Submit Request
        </button>

      </div>

    </div>

  </div>
)}

  </main>   
  );
};

export default Domain;
